import React, { useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';
import { Card, CardContent, CardHeader } from './ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState({
    totalReviews: 0,
    averageRating: 0
  });

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${API_URL}/api/reviews`);
        if (!response.ok) {
          throw new Error('Failed to fetch reviews');
        }
        const data = await response.json();
        setReviews(data.reviews || []);
        setMetadata({
          totalReviews: data.totalReviews || 0,
          averageRating: data.averageRating || 0
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError(err.message);
        setLoading(false);
        // Fallback to mock data on error
        import('../utils/mock').then(module => {
          setReviews(module.reviews);
        });
      }
    };

    fetchReviews();
  }, []);

  return (
    <section id="reviews" className="py-24 bg-black">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-red-600/20 border border-red-500/50 rounded-full px-4 py-2 mb-4">
            <span className="text-red-500 font-semibold text-sm">Testimonials</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            What Our Customers Say
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {loading ? (
              'Loading reviews...'
            ) : error ? (
              'Real reviews from satisfied customers who trust Mobile Shine for their detailing needs'
            ) : (
              `${metadata.totalReviews} reviews • ${metadata.averageRating.toFixed(1)} average rating on Google`
            )}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
            <p className="text-gray-400 mt-4">Fetching reviews from Google Business...</p>
          </div>
        )}

        {/* Reviews Grid */}
        {!loading && reviews.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {reviews.map((review) => (
            <Card
              key={review.id}
              className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-all duration-300"
            >
              <CardHeader>
                {/* Rating Stars */}
                <div className="flex items-center space-x-1 mb-3">
                  {[...Array(review.rating)].map((_, starIndex) => (
                    <Star
                      key={`${review.id}-star-${starIndex}`}
                      className="w-5 h-5 fill-red-500 text-red-500"
                    />
                  ))}
                  {[...Array(5 - review.rating)].map((_, starIndex) => (
                    <Star
                      key={`${review.id}-empty-${starIndex}`}
                      className="w-5 h-5 text-gray-600"
                    />
                  ))}
                </div>

                {/* Customer Info */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-white font-bold text-lg">{review.name}</h3>
                    <p className="text-gray-400 text-sm">{review.date}</p>
                  </div>
                  <Quote className="w-8 h-8 text-red-500/30" />
                </div>
              </CardHeader>

              <CardContent>
                {/* Review Text */}
                <p className="text-gray-300 leading-relaxed mb-4">
                  {review.review}
                </p>

                {/* Service Badge */}
                {review.vehicle && review.service && (
                  <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                    <span className="text-xs text-gray-400">{review.vehicle}</span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-red-600/20 border border-red-500/50 text-red-500 text-xs font-semibold">
                      {review.service}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-8 text-gray-400">
            <p>Currently showing sample reviews. Live reviews will load from your Google Business page.</p>
          </div>
        )}

        {/* CTA */}
        <div className="text-center space-y-6">
          {/* Google Review CTA */}
          <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 border border-red-500/30 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-3">Love Our Service?</h3>
            <p className="text-gray-300 mb-6">
              Share your experience and help others discover Mobile Shine!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://g.page/r/CS8IeFV59cyJEBM/review"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-red-500/50"
              >
                <span className="text-2xl">⭐</span>
                <span>Leave a Google Review</span>
              </a>
              <a
                href="https://g.page/r/CS8IeFV59cyJEBM"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-zinc-800 hover:bg-zinc-700 border-2 border-zinc-700 hover:border-red-500 text-white font-bold py-4 px-8 rounded-lg transition-all duration-300"
              >
                <span className="text-2xl">🔍</span>
                <span>View Our Google Business</span>
              </a>
            </div>
          </div>

          <p className="text-gray-400 text-lg">
            Ready to experience premium detailing?{' '}
            <button
              onClick={() => {
                const element = document.getElementById('booking');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-red-500 hover:text-red-400 font-semibold underline"
            >
              Book your service today
            </button>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
